import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  ChartLine, Robot, Star, Trophy, EnvelopeSimple,
  Wallet, MagnifyingGlass, Storefront, House,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { SymbolLogo } from "@/components/SymbolSearchInput";
import { playFlipChime } from "@/lib/chime";

/**
 * Cmd-K-style spotlight for STRATOS. Opens on ``/`` (and Cmd/Ctrl+K), lets
 * users jump to any page or straight into ``/trade?s=<SYMBOL>`` via the
 * live symbol autocomplete. Skips keydown hijacking while typing in inputs.
 *
 * Actions are static — nav routes + a shortcut to open the New Bot form.
 * Symbol suggestions come from ``/market/search?q=`` and are debounced 150ms
 * so we don't hammer yfinance for every keystroke.
 */
const ACTIONS = [
  { id: "nav-dashboard",   label: "Go to Dashboard",   Icon: House,        to: "/" },
  { id: "nav-trade",       label: "Go to Trade",       Icon: ChartLine,    to: "/trade" },
  { id: "nav-positions",   label: "Go to Positions",   Icon: Wallet,       to: "/positions" },
  { id: "nav-bots",        label: "Go to Auto Bots",   Icon: Robot,        to: "/bots" },
  { id: "nav-watchlist",   label: "Go to Watchlist",   Icon: Star,         to: "/watchlist" },
  { id: "nav-leaderboard", label: "Go to Leaderboard", Icon: Trophy,       to: "/leaderboard" },
  { id: "nav-alerts",      label: "Go to Alerts",      Icon: EnvelopeSimple, to: "/alerts" },
  { id: "nav-broker",      label: "Go to Broker",      Icon: Storefront,   to: "/broker" },
  { id: "act-deploy-bot",  label: "Deploy a new bot",  Icon: Robot,        to: "/bots?deploy=1" },
];

const RECENTS_KEY = "stratos.palette.recentSymbols";
const RECENTS_MAX = 5;

function loadRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENTS_MAX) : [];
  } catch { return []; }
}

function pushRecent(symbol, name, logo, visitedPrice) {
  try {
    const cur = loadRecents().filter((r) => r.symbol !== symbol);
    // Iter 47 — persist the CDN logo so recent-symbol rows can render the
    // real brand mark instead of a generic search icon. Falls back to
    // initials automatically inside <SymbolLogo>.
    // Iter 55 — also snapshot the price at "visit time" so the row-hover
    // peek can render a "+/-% since visit" chip, turning recents into a
    // mini watchlist without a dedicated backend.
    const next = [{
      symbol,
      name,
      logo: logo || "",
      visited_at: new Date().toISOString(),
      visited_price: (typeof visitedPrice === "number" && Number.isFinite(visitedPrice)) ? visitedPrice : null,
    }, ...cur].slice(0, RECENTS_MAX);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {}
}

function clearRecents() {
  try { localStorage.removeItem(RECENTS_KEY); } catch {}
}

export default function CommandPaletteSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [symbols, setSymbols] = useState([]);
  const [recents, setRecents] = useState(() => loadRecents());
  // Iter 48 — palette recent prices. Map keyed by symbol → { price, change_pct }
  // populated lazily whenever the palette re-opens. Cheap: at most
  // RECENTS_MAX (5) quotes fetched in parallel per open, cached backend-side
  // for 10 seconds. Missing entries render the row without a chip so the
  // palette stays useful even if the network hiccups.
  const [recentQuotes, setRecentQuotes] = useState({});
  // Iter 50 — palette chip sparkline. Map keyed by symbol → array of
  // closing prices (last ~2 days of hourly bars). Fetched once per palette
  // open — history rarely swings inside a single session so no live-tick
  // needed. Missing entries just render the chip without a sparkline.
  const [recentSparks, setRecentSparks] = useState({});
  // Iter 54 — track which recent row is currently hovered so we can
  // simultaneously highlight the trend arrow AND surface the 5d peek
  // text below the row.
  const [hoveredRecent, setHoveredRecent] = useState(null);
  // Iter 58 — Logo Ring Pulse. When the index-0 since-visit delta
  // crosses zero (positive↔negative) between live-tick refreshes, we
  // pulse the ring briefly so admins notice the flip. Store the last
  // seen sign per-symbol via a ref (no re-render needed) and set
  // ``pulsingSymbol`` (state) briefly for the animation class.
  const lastDeltaSignRef = useRef({});
  const [pulsingSymbol, setPulsingSymbol] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const isTyping = (t) => {
      if (!t) return false;
      const tag = (t.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || t.isContentEditable;
    };
    const handler = (e) => {
      // Cmd/Ctrl+K opens regardless of focus (matches Cmd-K convention).
      if ((e.metaKey || e.ctrlKey) && e.key?.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      // Plain "/" only opens when not typing — otherwise we'd hijack the
      // Spotify-style search inputs elsewhere on the page.
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Debounced symbol search — 150ms is snappy enough that the results feel
  // instant while dropping ~80% of intermediate keystroke requests.
  useEffect(() => {
    if (!query || query.length < 1) { setSymbols([]); return; }
    const t = setTimeout(() => {
      api.get(`/market/search?q=${encodeURIComponent(query)}`)
        .then((r) => setSymbols(r.data?.results?.slice(0, 6) || []))
        .catch(() => setSymbols([]));
    }, 150);
    return () => clearTimeout(t);
  }, [query]);

  // Re-hydrate recents whenever the palette re-opens — another tab or a
  // navigation may have pushed a new entry while this instance was closed.
  useEffect(() => {
    if (open) setRecents(loadRecents());
  }, [open]);

  // Iter 48 — fetch fresh price + day-change % for each recent symbol on
  // every palette open. Runs in parallel; individual failures are swallowed
  // so one dead ticker never poisons the batch. Merges into the map so
  // stale entries from a prior open remain visible while the new fetch
  // resolves.
  // Iter 49 — live-tick: re-fetch every 15s while the palette is open so
  // an idle session doesn't drift stale. Interval is cleared on close so
  // we never poll a hidden dialog. 15s aligns with the yfinance cache TTL
  // ceiling — anything faster wastes cycles hitting the cache layer.
  useEffect(() => {
    if (!open) return;
    const fetchAll = () => {
      const list = loadRecents();
      if (list.length === 0) return;
      Promise.all(
        list.map((r) =>
          api.get(`/market/quote/${encodeURIComponent(r.symbol)}`)
            .then((res) => ({ symbol: r.symbol, price: res.data?.price, change_pct: res.data?.change_pct }))
            .catch(() => null)
        )
      ).then((results) => {
        setRecentQuotes((prev) => {
          const next = { ...prev };
          for (const q of results) {
            if (q && typeof q.price === "number") next[q.symbol] = q;
          }
          return next;
        });
      });
    };
    fetchAll();
    const interval = setInterval(fetchAll, 15_000);
    return () => clearInterval(interval);
  }, [open]);

  // Iter 50 — palette chip sparkline. Fetch ~2 days of hourly closes for
  // each recent so the chip can render a mini trend line behind the price.
  // Only runs once per palette open (unlike the price live-tick) because
  // history doesn't swing meaningfully inside a session. Missing symbols
  // just skip the sparkline gracefully — the chip still shows price+%.
  useEffect(() => {
    if (!open) return;
    const list = loadRecents();
    if (list.length === 0) return;
    let cancelled = false;
    Promise.all(
      list.map((r) =>
        api.get(`/market/history/${encodeURIComponent(r.symbol)}?period=5d&interval=1h`)
          .then((res) => {
            const bars = res.data?.bars || [];
            // Extract just the closing prices; the sparkline component
            // handles empty arrays gracefully. History bars use the short
            // OHLCV key convention (`c` = close) — fall back to `close`
            // in case an older cached response format sneaks through.
            const closes = bars
              .map((b) => (typeof b.c === "number" ? b.c : b.close))
              .filter((n) => typeof n === "number");
            return { symbol: r.symbol, closes };
          })
          .catch(() => null)
      )
    ).then((results) => {
      if (cancelled) return;
      setRecentSparks((prev) => {
        const next = { ...prev };
        for (const s of results) {
          if (s && s.closes.length > 1) next[s.symbol] = s.closes;
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [open]);

  // Iter 58 — detect index-0 since-visit delta crossing zero and fire a
  // one-shot pulse so admins notice the flip live. Runs whenever the
  // recentQuotes map updates (i.e. after every 15s live-tick).
  useEffect(() => {
    if (recents.length === 0) return;
    const top = recents[0];
    const q = recentQuotes[top.symbol];
    if (!q || typeof q.price !== "number" || typeof top.visited_price !== "number" || top.visited_price <= 0) return;
    const delta = ((q.price - top.visited_price) / top.visited_price) * 100;
    const sign = delta >= 0 ? "+" : "-";
    const prev = lastDeltaSignRef.current[top.symbol];
    if (prev !== undefined && prev !== sign) {
      // Sign flipped — pulse for ~1s then clear.
      setPulsingSymbol(top.symbol);
      // Iter 59 — audible cue on the flip too, so admins on other tabs
      // still hear the transition. ``playChime`` no-ops when muted or
      // when no user gesture has unlocked the AudioContext yet.
      // Iter 60 — swapped to the ascending C→E `playFlipChime` so admins
      // can identify the sign-flip event by ear (vs the descending E→C
      // general chime).
      playFlipChime();
      const t = setTimeout(() => setPulsingSymbol(null), 1000);
      lastDeltaSignRef.current[top.symbol] = sign;
      return () => clearTimeout(t);
    }
    lastDeltaSignRef.current[top.symbol] = sign;
  }, [recentQuotes, recents]);

  const run = (fn) => {
    setOpen(false);
    setQuery("");
    setSymbols([]);
    // Defer to next tick so the dialog animation doesn't fight the route change.
    setTimeout(fn, 20);
  };

  const jumpSymbol = (symbol, name, logo, priceAtVisit) => {
    pushRecent(symbol, name || "", logo || "", priceAtVisit);
    setRecents(loadRecents());
    run(() => navigate(`/trade?s=${symbol}`));
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <div data-testid="command-palette" />
      <CommandInput
        data-testid="command-palette-input"
        placeholder="Jump to a symbol or action…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No matches for "{query}"</CommandEmpty>
        {/* Recent symbols — only shown on an empty query so muscle-memory
            gets a one-tap surface without cluttering active search. */}
        {!query && recents.length > 0 && (
          <CommandGroup
            heading={
              <div className="flex items-center justify-between w-full">
                <span>Recent</span>
                <button
                  type="button"
                  data-testid="command-recent-clear"
                  onClick={(e) => {
                    // Stop cmdk from treating this as an item selection —
                    // clicking Clear should wipe recents in place, not
                    // navigate anywhere.
                    e.stopPropagation();
                    e.preventDefault();
                    clearRecents();
                    setRecents([]);
                  }}
                  className="mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-red-400 transition-colors"
                >
                  Clear
                </button>
              </div>
            }
          >
            {recents.map((r, idx) => {
              const q = recentQuotes[r.symbol];
              const spark = recentSparks[r.symbol];
              const hovered = hoveredRecent === r.symbol;
              // Iter 55 — since-visit delta: compare the current quote to
              // the price we snapshotted when the row was first added.
              // Null when either side is missing (fresh recents, cold
              // quote fetch, or pre-Iter-55 legacy entries).
              const sinceDelta =
                q && typeof q.price === "number" && typeof r.visited_price === "number" && r.visited_price > 0
                  ? ((q.price - r.visited_price) / r.visited_price) * 100
                  : null;
              // Iter 56 — the top-of-list "last-visited" row shows its
              // since-visit chip inline (always visible), so its
              // performance is glanceable at rest. Other rows still
              // reveal it in the hover peek.
              const inlineSinceVisit = idx === 0 && sinceDelta != null;
              const peekLine = spark && spark.length > 1
                ? `5d: $${spark[0].toFixed(2)} → $${spark[spark.length - 1].toFixed(2)}`
                : null;
              return (
                <CommandItem
                  key={`recent-${r.symbol}`}
                  value={`recent-${r.symbol}`}
                  data-testid={`command-recent-${r.symbol}`}
                  onSelect={() => jumpSymbol(r.symbol, r.name, r.logo, q?.price)}
                  onMouseEnter={() => setHoveredRecent(r.symbol)}
                  onMouseLeave={() => setHoveredRecent(null)}
                  className="flex-col items-stretch"
                >
                  <div className="flex items-center w-full">
                    {/* Iter 56/57 — the last-visited row telegraphs its
                        since-visit delta on the logo border too, so the
                        whole row reads as one green/red pill.
                        Iter 58 — one-shot pulse animation (animate-ping
                        halo overlay) when the delta crosses zero between
                        live-ticks so the flip is impossible to miss. */}
                    <span
                      data-testid={`command-recent-logo-${r.symbol}`}
                      data-since-tone={
                        inlineSinceVisit
                          ? (sinceDelta >= 0 ? "up" : "down")
                          : "none"
                      }
                      data-pulsing={pulsingSymbol === r.symbol ? "true" : "false"}
                      className={
                        "mr-2 flex items-center rounded-full transition-shadow duration-200 relative " +
                        (inlineSinceVisit
                          ? (sinceDelta >= 0
                              ? "ring-1 ring-[#00C805]/60 shadow-[0_0_0_2px_rgba(0,200,5,0.08)]"
                              : "ring-1 ring-[#FF3B30]/60 shadow-[0_0_0_2px_rgba(255,59,48,0.08)]")
                          : "")
                      }
                    >
                      {pulsingSymbol === r.symbol && (
                        <span
                          aria-hidden="true"
                          className={
                            "absolute inset-0 rounded-full animate-ping " +
                            (sinceDelta >= 0
                              ? "bg-[#00C805]/40"
                              : "bg-[#FF3B30]/40")
                          }
                        />
                      )}
                      <SymbolLogo symbol={r.symbol} logo={r.logo} size={16} />
                    </span>
                    <span className="font-bold mono">{r.symbol}</span>
                    {r.name && <span className="ml-2 text-zinc-500 text-xs truncate">{r.name}</span>}
                    {q && typeof q.price === "number" && (
                      <span
                        data-testid={`command-recent-price-${r.symbol}`}
                        className="ml-auto pl-3 flex items-center gap-2 mono text-[10px]"
                      >
                        {spark && (
                          <MiniSparkline
                            data-testid={`command-recent-sparkline-${r.symbol}`}
                            symbol={r.symbol}
                            closes={spark}
                            up={q.change_pct >= 0}
                          />
                        )}
                        {/* Iter 53 — quick-glance direction arrow next to the
                            sparkline so users decode movement before parsing
                            the change-pct colour.
                            Iter 54 — row-hover highlights the arrow with a
                            scale-up + brightness bump so the sparkline range
                            correspondence pops. */}
                        <span
                          data-testid={`command-recent-arrow-${r.symbol}`}
                          data-direction={q.change_pct >= 0 ? "up" : "down"}
                          data-highlighted={hovered ? "true" : "false"}
                          className={
                            "text-[9px] transition-all duration-150 " +
                            (q.change_pct >= 0 ? "text-[#00C805]" : "text-[#FF3B30]") +
                            (hovered ? " scale-125 brightness-125" : "")
                          }
                        >
                          {q.change_pct >= 0 ? "▲" : "▼"}
                        </span>
                        <span className="text-zinc-300 tabular-nums">
                          ${q.price.toFixed(2)}
                        </span>
                        <span
                          data-testid={`command-recent-change-${r.symbol}`}
                          data-direction={q.change_pct >= 0 ? "up" : "down"}
                          className={
                            "tabular-nums " +
                            (q.change_pct >= 0 ? "text-[#00C805]" : "text-[#FF3B30]")
                          }
                        >
                          {q.change_pct >= 0 ? "+" : ""}
                          {q.change_pct?.toFixed(2)}%
                        </span>
                        {/* Iter 56 — inline since-visit chip for the
                            last-visited row (index 0). Glanceable at rest;
                            other rows keep the hover-peek treatment. */}
                        {inlineSinceVisit && (
                          <span
                            data-testid={`command-recent-since-visit-inline-${r.symbol}`}
                            data-direction={sinceDelta >= 0 ? "up" : "down"}
                            className={
                              "tabular-nums text-[9px] border rounded-sm px-1 py-0.5 " +
                              (sinceDelta >= 0
                                ? "text-[#00C805] border-[#00C805]/40 bg-[#00C805]/5"
                                : "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/5")
                            }
                            title="Change since you last visited this ticker"
                          >
                            since visit {sinceDelta >= 0 ? "+" : ""}{sinceDelta.toFixed(2)}%
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  {/* Iter 54 — peek line surfaces the 5d price range under
                      the row on hover. Complements the SVG <title> tooltip
                      by exposing the numbers without requiring the user to
                      pause exactly on the sparkline.
                      Iter 55 — appends a "since visit" delta so recents
                      double as a mini watchlist for symbols the user
                      recently opened. */}
                  {hovered && (peekLine || (sinceDelta != null && !inlineSinceVisit)) && (
                    <div
                      data-testid={`command-recent-peek-${r.symbol}`}
                      className="mono text-[9px] tracking-widest uppercase pl-6 pt-1 flex gap-3"
                    >
                      {peekLine && <span className="text-zinc-500">{peekLine}</span>}
                      {sinceDelta != null && !inlineSinceVisit && (
                        <span
                          data-testid={`command-recent-since-visit-${r.symbol}`}
                          data-direction={sinceDelta >= 0 ? "up" : "down"}
                          className={
                            "tabular-nums " +
                            (sinceDelta >= 0 ? "text-[#00C805]" : "text-[#FF3B30]")
                          }
                        >
                          Since visit: {sinceDelta >= 0 ? "+" : ""}{sinceDelta.toFixed(2)}%
                        </span>
                      )}
                    </div>
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
        {symbols.length > 0 && (
          <CommandGroup heading="Symbols">
            {symbols.map((s) => (
              <CommandItem
                key={s.symbol}
                value={`sym-${s.symbol}`}
                data-testid={`command-symbol-${s.symbol}`}
                onSelect={() => jumpSymbol(s.symbol, s.name, s.logo)}
              >
                <MagnifyingGlass size={14} className="mr-2 text-zinc-500" />
                <span className="font-bold mono">{s.symbol}</span>
                <span className="ml-2 text-zinc-500 text-xs truncate">{s.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup heading="Actions">
          {ACTIONS.map((a) => (
            <CommandItem
              key={a.id}
              value={a.label.toLowerCase()}
              data-testid={`command-action-${a.id}`}
              onSelect={() => run(() => navigate(a.to))}
            >
              <a.Icon size={14} className="mr-2 text-zinc-500" />
              {a.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/**
 * Iter 50 — tiny SVG sparkline for palette recent chips. Renders the last
 * ~24 closes so an entire 2-day trend fits in a 60x16 strip beside the
 * price. Colour matches the day-change tint so the whole chip reads as
 * one glance. Uses only the last N points to keep the DOM path short.
 */
function MiniSparkline({ symbol, closes, up, ...rest }) {
  if (!closes || closes.length < 2) return null;
  // Trim to a rolling window so the visual stays crisp; ~24 hourly closes
  // covers roughly the last two trading days without crushing the strip.
  const points = closes.slice(-24);
  const w = 60;
  const h = 16;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  // Guard against zero-range flat lines: place them at vertical mid so the
  // sparkline still communicates "no movement" instead of collapsing.
  const path = points
    .map((v, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const color = up ? "#00C805" : "#FF3B30";
  // Iter 52 — range highlight: mark the min and max points with tiny dots
  // so the peek numbers ($first → $last) correspond visually to the
  // shape. Uses the first occurrence of each extremum which is fine for
  // the sparse ~24-point window.
  const maxIdx = points.indexOf(max);
  const minIdx = points.indexOf(min);
  const dotX = (i) => ((i / (points.length - 1)) * w).toFixed(1);
  const dotY = (v) => (h - ((v - min) / range) * h).toFixed(1);
  // Iter 51 — hover peek: native SVG <title> renders the first→last price
  // range on hover. Cheap, accessible, and no tooltip library required.
  const first = points[0];
  const last = points[points.length - 1];
  const peek = `5d ${symbol}: $${first.toFixed(2)} → $${last.toFixed(2)}`;
  return (
    <svg
      data-testid={`command-recent-sparkline-${symbol}`}
      data-peek={peek}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="shrink-0 opacity-70"
      {...rest}
    >
      <title>{peek}</title>
      <path d={path} fill="none" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
      {/* Max dot in profit green, min dot in loss red — always the same
          colours regardless of overall direction so users instantly
          decode which is which. */}
      <circle
        data-testid={`command-recent-sparkline-${symbol}-max`}
        cx={dotX(maxIdx)}
        cy={dotY(max)}
        r={1.5}
        fill="#00C805"
      />
      <circle
        data-testid={`command-recent-sparkline-${symbol}-min`}
        cx={dotX(minIdx)}
        cy={dotY(min)}
        r={1.5}
        fill="#FF3B30"
      />
    </svg>
  );
}

